import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, ExternalLink, Zap } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin Settings card — one-glance production-readiness dashboard.
 * Consumes GET /api/admin/system/health (secrets stay server-side; we only
 * see presence + mode) and POST /api/admin/system/stripe-smoke (creates a
 * real $1 checkout session the admin can complete on their live keys).
 */
export function SystemHealthCard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [smoking, setSmoking] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [h, s] = await Promise.all([
        api.get("/admin/system/health"),
        api.get("/admin/system/stripe-smoke/history"),
      ]);
      setData(h.data);
      setHistory(s.data.items || []);
    } catch (e) {
      setErr(apiErrorMessage(e) || "Could not run health check");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSmoke = async () => {
    if (!window.confirm(
      "This creates a REAL $1.00 Stripe Checkout session. You'll be charged $1.00 on the card you enter, and can refund it after. Continue?"
    )) return;
    setSmoking(true);
    try {
      const { data: res } = await api.post("/admin/system/stripe-smoke", { amount: 1.0 });
      toast.success("Smoke test session created — opening Stripe Checkout");
      window.open(res.url, "_blank", "noopener,noreferrer");
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Smoke test failed");
    } finally {
      setSmoking(false);
    }
  };

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="system-health-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Production readiness</div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            <Zap className="h-4 w-4 text-orange-400" /> System health
          </div>
          <div className="text-sm text-zinc-400 mt-1">
            Everything the client needs to flip live. Secrets stay on the server — only presence + mode are shown.
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="border-white/10" data-testid="system-health-refresh">
          <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (loading ? "animate-spin" : "")} /> Recheck
        </Button>
      </div>

      {err && <div className="text-xs text-red-300 mb-3">{err}</div>}

      {data && (
        <>
          {/* Overall readiness banner */}
          <div
            className={
              "rounded-lg px-4 py-3 mb-4 border flex items-center justify-between gap-3 " +
              (data.ready_for_production
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200"
                : "bg-amber-500/10 border-amber-500/30 text-amber-200")
            }
            data-testid={data.ready_for_production ? "system-ready" : "system-not-ready"}
          >
            <div className="flex items-center gap-2">
              {data.ready_for_production
                ? <CheckCircle2 className="h-5 w-5" />
                : <AlertTriangle className="h-5 w-5" />}
              <div>
                <div className="font-semibold text-sm">
                  {data.ready_for_production ? "Ready for production" : "Not production-ready yet"}
                </div>
                <div className="text-[11px] opacity-80">
                  Stripe: <b>{data.stripe_mode}</b> · Public URL: {data.public_app_url ? <b>{data.public_app_url.replace(/^https?:\/\//, "")}</b> : <b>unset</b>}
                </div>
              </div>
            </div>
            <Badge variant="outline" className={data.stripe_mode === "live" ? "border-emerald-400 text-emerald-200" : "border-amber-400 text-amber-200"}>
              Stripe {data.stripe_mode}
            </Badge>
          </div>

          {/* Per-check list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            {(data.checks || []).map((c) => (
              <div
                key={c.id}
                className={"rounded-md border px-3 py-2 flex items-center gap-2 text-xs " + (c.ok ? "border-emerald-500/20 bg-emerald-500/[0.04]" : "border-amber-500/20 bg-amber-500/[0.04]")}
                data-testid={`system-check-${c.id}`}
              >
                {c.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-zinc-200 truncate">{c.label}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{c.detail}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Reset / verify email link previews */}
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 mb-4">
            <div className="text-xs text-zinc-400 mb-2">Auth email link previews</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-[11px]">
                <Badge variant="outline" className="border-white/10 text-zinc-300">reset</Badge>
                <span className="font-mono text-zinc-400 truncate" data-testid="system-reset-preview">{data.reset_link_preview || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-[11px]">
                <Badge variant="outline" className="border-white/10 text-zinc-300">verify</Badge>
                <span className="font-mono text-zinc-400 truncate" data-testid="system-verify-preview">{data.verify_link_preview || "—"}</span>
              </div>
            </div>
          </div>

          {/* Stripe smoke test */}
          <div className="rounded-md border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white">Live payments smoke test</div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  Creates a real $1.00 Stripe Checkout session on the currently-configured key. Complete it on any card, then refund from Admin → Payments to close the loop.
                </div>
              </div>
              <Button
                size="sm"
                onClick={runSmoke}
                disabled={smoking || data.stripe_mode === "missing"}
                className="rounded-full bg-orange-500 hover:bg-orange-600 shrink-0"
                data-testid="system-run-stripe-smoke"
              >
                {smoking ? "Opening…" : "Run $1 smoke test"}
              </Button>
            </div>

            {history.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/5 space-y-1.5">
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">Recent smoke tests</div>
                {history.slice(0, 5).map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-3 text-[11px]" data-testid="system-smoke-row">
                    <div className="text-zinc-400 truncate">
                      ${Number(r.amount || 0).toFixed(2)} · <span className="text-zinc-500">{r.actor_email}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="text-orange-300 hover:text-orange-200 inline-flex items-center gap-1">
                        Open <ExternalLink className="h-3 w-3" />
                      </a>
                      <span className="text-zinc-600 tabular-nums">{new Date((r.at || 0) * 1000).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
